import { useState } from "react";
import { Formik } from "formik";
import {
  Button,
  Divider,
  Grid,
  InputAdornment,
  TextField,
  Typography
} from "@mui/material";

import ImageUploader, {
  uploadBas64ToS3
} from "../../../../components/utilities/ImageUploader";
import HelpButton from "../../../../components/utilities/HelpButton";
import { postNotification } from "../../../../components/utilities/Notifications";
import ModalLoadingFull from "../../../../components/modals/ModalLoadingFull";
import { postData } from "../../../../components/utilities/UtilityFunctions";
import { getLanguageCookie } from "../../../../language/LanguageFunctions";
import { useTranslation } from "react-i18next";

export default function ProfileTab(props) {
  const { user, setUser } = props;

  // Language setup
  const language = getLanguageCookie();
  const { t } = useTranslation("translation", {
    lng: language,
    keyPrefix: "pages.account.profile"
  });

  const [processing, setProcessing] = useState(false);
  const socialOptions = [
    { key: "Facebook", prefix: "facebook.com/" },
    { key: "Instagram", prefix: "instagram.com/" },
    { key: "Twitter", prefix: "twitter.com/" },
    { key: "Twitch", prefix: "twitch.tv/" },
    { key: "YouTube", prefix: "youtube.com/" },
    {
      key: "Discord",
      prefix: "Discord ID",
      InputProps: {
        endAdornment: (
          <InputAdornment position="end">
            <HelpButton
              title={t("tooltips.discord", {
                defaultValue: `In Discord, select Settings > Advanced > Developer Mode. Once the Developer Mode is on, right click on your User Name in any chat window and select "Copy ID". This should be a string of 18 numbers`
              })}
            />
          </InputAdornment>
        )
      }
    }
  ];
  return (
    <>
      <Formik
        initialValues={{
          profileFileId: user?.profileFileId || "",
          bannerFileId: user?.bannerFileId || "",
          nickname: user?.nickname || "",
          bio: user?.bio || "",
          socials: user?.socials || {}
        }}
        onSubmit={async (values) => {
          try {
            setProcessing(true);
            if (values.profileImage) {
              // upload image to s3
              let newFile = await uploadBas64ToS3(values.profileImage);
              delete values.profileImage;
              if (newFile.fileId) {
                values.profileFileId = newFile.fileId;
              }
            }
            if (values.bannerImage) {
              // upload image to s3
              let newFile = await uploadBas64ToS3(values.bannerImage);
              delete values.bannerImage;
              if (newFile.fileId) {
                values.bannerFileId = newFile.fileId;
              }
            }

            if (!values.bannerFileId || values.bannerFileId === "") {
              delete values.bannerFileId;
            }

            if (!values.profileFileId || values.profileFileId === "") {
              delete values.profileFileId;
            }

            var apiUser = await postData(`/users/${user.id}`, {
              body: {
                set: values
              }
            });

            setUser({
              ...user,
              ...apiUser
            });
            postNotification("success", t("notifications.success.userUpdated"));
          } catch (err) {
            postNotification("error", err);
          }
          setProcessing(false);
        }}
      >
        {({
          errors,
          handleBlur,
          handleChange,
          handleSubmit,
          setFieldValue,
          touched,
          values
        }) => (
          <form noValidate onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: "700" }}
                  gutterBottom
                >
                  {t("profilePicture", {
                    defaultValue: "Profile Picture"
                  })}
                </Typography>
                <ImageUploader
                  values={values}
                  label={`${user?.firstName || ""} ${
                    user?.lastName || ""
                  }`.trim()}
                  name="profileImage"
                  setFieldValue={setFieldValue}
                  fileId={values.profileFileId}
                  defaultUrl={user?.profileFile?.url}
                  aspectW={1}
                  aspectY={1}
                />
              </Grid>
              <Grid item xs={12} md={8}>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: "700" }}
                  gutterBottom
                >
                  {t("coverPhoto", {
                    defaultValue: "Cover Photo"
                  })}
                </Typography>
                <ImageUploader
                  values={values}
                  label={`${user?.firstName || ""} ${
                    user?.lastName || ""
                  }`.trim()}
                  fileId={values.bannerFileId}
                  name="bannerImage"
                  setFieldValue={setFieldValue}
                  defaultUrl={user?.bannerFile?.url}
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ mt: 1, mb: 1 }} />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h4" fontWeight="700" gutterBottom>
                  {t("profile", {
                    defaultValue: "Profile"
                  })}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  error={Boolean(touched.nickname && errors.nickname)}
                  helperText={touched.nickname && errors.nickname}
                  name="nickname"
                  value={values.nickname}
                  label={t("nickname", {
                    defaultValue: "Nickname",
                    keyPrefix: "fields"
                  })}
                  variant="outlined"
                  fullWidth
                  onBlur={handleBlur}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  error={Boolean(touched.bio && errors.bio)}
                  helperText={touched.bio && errors.bio}
                  name="bio"
                  value={values.bio}
                  label={t("bio", {
                    defaultValue: "Bio",
                    keyPrefix: "fields"
                  })}
                  variant="outlined"
                  fullWidth
                  onBlur={handleBlur}
                  onChange={handleChange}
                  multiline
                  rows={5}
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ mt: 1, mb: 1 }} />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h4" fontWeight="700" gutterBottom>
                  {t("socials", {
                    defaultValue: "Socials"
                  })}
                </Typography>
              </Grid>
              {socialOptions.map((socialOption, index) => {
                const { key, prefix, InputProps } = socialOption;
                return (
                  <Grid item xs={12} key={index}>
                    <TextField
                      name={`socials.${key}`}
                      value={values.socials?.[key]}
                      label={t(key?.toLowerCase(), {
                        defaultValue: key,
                        keyPrefix: "misc.socials"
                      })}
                      variant="outlined"
                      fullWidth
                      onBlur={handleBlur}
                      onChange={handleChange}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            {prefix}
                          </InputAdornment>
                        ),
                        ...InputProps
                      }}
                    />
                  </Grid>
                );
              })}

              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  color="success"
                  fullWidth
                >
                  {t("saveChanges", {
                    defaultValue: "Save Changes",
                    keyPrefix: "buttons"
                  })}
                </Button>
              </Grid>
            </Grid>
          </form>
        )}
      </Formik>
      {Boolean(processing) && <ModalLoadingFull />}
    </>
  );
}
